import { ErrorMessages, SnackbarAlertTypes } from "@/shared/common/enums";
import type { IError, TNotificationScope } from "@/shared/common/types";
import { useAppDispatch } from "@/shared/hooks/reduxHooks";
import { setAlertNotification, setSnackbarNotification } from "@/shared/state/notification";
import type {
  IAlertNotification,
  ISnackbarNotification
} from "@/shared/state/notification/notificationSlice";
import { notify } from "@ab.uitools/ui-library/components/Snackbar";
import type { TSnackbarTypes } from "@ab.uitools/ui-library/components/Snackbar/types";
import { useTranslation } from "react-i18next";
import uuid4 from "uuid4";

interface IShowSnackbarProps {
  message: string;
  type: TSnackbarTypes
}

export const useNotify = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const showErrorAlertNotification = (error: IError | string, scope?: TNotificationScope) => {
    const message = typeof error === "string" ? error : error?.data?.warnings?.[0];
    dispatch(setAlertNotification({
      type: SnackbarAlertTypes.Error,
      message: t(`${message || ErrorMessages.TechnicalDifficulties}`),
      scope,
    }));
  };

  const showSuccessAlertNotification = (message: string, scope?: TNotificationScope) => {
    dispatch(setAlertNotification({
      type: SnackbarAlertTypes.Success,
      message: message || t(ErrorMessages.TechnicalDifficulties),
      scope,
    }));
  };

  const showWarningAlertNotification = (message: string, scope?: TNotificationScope) => {
    dispatch(setAlertNotification({
      type: SnackbarAlertTypes.Warning,
      message: message,
      scope,
    }));
  };

  const clearAlertNotification = () => {
    dispatch(setAlertNotification({} as IAlertNotification));
  };

  const showSuccessSnackbarNotification = (message: string) => {
    dispatch(setSnackbarNotification({
      message,
      type: SnackbarAlertTypes.Success,
    }));
  };

  const clearSnackbarNotification = () => {
    dispatch(setSnackbarNotification({} as ISnackbarNotification));
  };

  const showSnackbar = ({ message, type }: IShowSnackbarProps) => {
    const id = uuid4();
    notify({
      text: message,
      type,
      toastId: id,
    });
  };

  const showErrorSnackbar = (error?: IError) => {
    showSnackbar({
      message: error?.data?.warnings?.[0] || t(ErrorMessages.TechnicalDifficulties),
      type: SnackbarAlertTypes.Error,
    });
  };

  const showSuccessSnackbar = (message: string) => {
    showSnackbar({
      message: message,
      type: SnackbarAlertTypes.Success,
    });
  };

  return {
    showErrorAlertNotification,
    showSuccessAlertNotification,
    showWarningAlertNotification,
    clearAlertNotification,
    showSuccessSnackbarNotification,
    clearSnackbarNotification,
    showErrorSnackbar,
    showSnackbar,
    showSuccessSnackbar,
  };
};
